import { Button } from '@creditornot/cb-button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { approvePhotoShoot, downloadImages, submitPhotoShoot } from 'apis/mips/api';
import { PhotoShootImageData, PhotoShootStatus } from 'apis/mips/types';
import { ConfirmModal } from 'common/components';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { getErrorContent } from 'common/utils';

import { PhotoShootQueryKeys } from '../constants/queryKeys';
import { routes } from '../constants/routes';
import usePhotoShootContext from '../hooks/usePhotoShootContext';
import { PhotoShootItem } from '../types';
import { downloadFile, fetchZipBlob } from '../utils/photoShootUtils';

export type PhotoShootActionsProps = {
  images: PhotoShootImageData[];
  allItems: PhotoShootItem[];
  status: PhotoShootStatus;
};

const Root = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const StyledModal = styled(ConfirmModal)`
  width: 100%;
  max-width: 700px;
`;

const handleError = (error: unknown) => toast.error(getErrorContent(error) || '');

export const PhotoShootActions = ({ images, status }: PhotoShootActionsProps) => {
  const [isSubmitConfirmationModalOpen, setIsSubmitConfirmationModalOpen] = useState(false);
  const [isApproveConfirmationModalOpen, setIsApproveConfirmationModalOpen] = useState(false);
  const { venueId, venueName, token } = usePhotoShootContext();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { search } = useLocation();

  const imagesIds = images.map(image => image.id);

  const { mutate: handleZipDownload, isLoading: isDownloadZipLoading } = useMutation(
    PhotoShootQueryKeys.DOWNLOAD_ZIP(imagesIds, venueId, token),
    async () => {
      if (images.length === 0) {
        return;
      }

      const resp = await downloadImages(venueId, imagesIds, token);
      const blob = await fetchZipBlob(resp.downloadUrls);
      downloadFile(blob, venueName);
    },
    {
      onError: error => toast.error(getErrorContent(error) || ''),
    },
  );

  const { mutate: handleSubmit, isLoading: isSubmitLoading } = useMutation(() => submitPhotoShoot(venueId, token), {
    onSuccess: res => {
      queryClient.setQueryData(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token), res.data);
      history.push(`${routes.imagesSubmitted(venueId)}${search}`);
    },
    onError: handleError,
    onSettled: () => setIsSubmitConfirmationModalOpen(false),
  });

  const { mutate: handleApprove, isLoading: isApproveLoading } = useMutation(() => approvePhotoShoot(venueId, token), {
    onSuccess: res => {
      queryClient.setQueryData(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token), res.data);
      history.push(`${routes.imagesApproved(venueId)}${search}`);
    },
    onError: handleError,
    onSettled: () => setIsApproveConfirmationModalOpen(false),
  });

  return (
    <Root>
      <Button
        size="small"
        variant="secondary"
        onClick={() => handleZipDownload()}
        loading={isDownloadZipLoading}
        disabled={images.length === 0}
      >
        <FormattedMessage id="photo-shoot.download-zip-button" />
      </Button>
      {status === PhotoShootStatus.CREATED && (
        <Button
          size="small"
          variant="primary"
          onClick={() => setIsSubmitConfirmationModalOpen(true)}
          disabled={images.length === 0}
          loading={isSubmitLoading}
        >
          <FormattedMessage id="photo-shoot.submit-button" />
        </Button>
      )}
      <StyledModal
        header={<FormattedMessage id="photo-shoot.submit-confirmation-dialog.header" />}
        description={<FormattedMessage id="photo-shoot.submit-confirmation-dialog.description" />}
        cancelText={<FormattedMessage id="photo-shoot.submit-confirmation-dialog.cancel" />}
        confirmText={<FormattedMessage id="photo-shoot.submit-confirmation-dialog.confirm" />}
        onCancel={() => setIsSubmitConfirmationModalOpen(false)}
        onConfirm={handleSubmit}
        open={isSubmitConfirmationModalOpen}
        loading={isSubmitLoading}
        disabled={isSubmitLoading}
      />
      <StyledModal
        header={<FormattedMessage id="photo-shoot.approve-confirmation-dialog.header" />}
        description={<FormattedMessage id="photo-shoot.approve-confirmation-dialog.description" />}
        cancelText={<FormattedMessage id="photo-shoot.approve-confirmation-dialog.cancel" />}
        confirmText={<FormattedMessage id="photo-shoot.approve-confirmation-dialog.confirm" />}
        onCancel={() => setIsApproveConfirmationModalOpen(false)}
        onConfirm={handleApprove}
        open={isApproveConfirmationModalOpen}
        loading={isApproveLoading}
        disabled={isApproveLoading}
      />
    </Root>
  );
};
